import { useEffect, useRef, useState } from "react";
import { RotateCw } from "lucide-react";
import { isStandalone } from "@/lib/standalone";

/**
 * Pull-to-refresh gesture for installed PWAs. Standalone mode has no browser
 * chrome, so the native pull-to-refresh is gone. This re-creates it: when the
 * active scroll container is at the top and the user drags down past a
 * threshold, the app reloads.
 *
 * Only active in standalone mode (desktop/browser tabs keep their native
 * reload). Mounted once globally from AppShell.
 */
const THRESHOLD = 70;
const MAX_PULL = 110;
const RESISTANCE = 0.5;

function scrollableAncestorAtTop(target: EventTarget | null): boolean {
    let node = target as HTMLElement | null;
    while (node && node !== document.body) {
        if (node.nodeType === 1) {
            const style = window.getComputedStyle(node);
            const oy = style.overflowY;
            if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) {
                return node.scrollTop <= 0;
            }
        }
        node = node.parentElement;
    }
    const root = document.scrollingElement || document.documentElement;
    return (root?.scrollTop ?? 0) <= 0;
}

export function PullToRefresh() {
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startY = useRef<number | null>(null);
    const canPull = useRef(false);

    useEffect(() => {
        if (!isStandalone()) return;

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) {
                canPull.current = false;
                return;
            }
            startY.current = e.touches[0].clientY;
            canPull.current = scrollableAncestorAtTop(e.target);
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!canPull.current || startY.current === null) return;
            const delta = e.touches[0].clientY - startY.current;
            if (delta <= 0) {
                setPull(0);
                return;
            }
            // Take over the gesture once we're clearly pulling down from the top.
            if (e.cancelable) e.preventDefault();
            setPull(Math.min(delta * RESISTANCE, MAX_PULL));
        };

        const onTouchEnd = () => {
            if (canPull.current && pull >= THRESHOLD) {
                setRefreshing(true);
                window.location.reload();
            } else {
                setPull(0);
            }
            startY.current = null;
            canPull.current = false;
        };

        window.addEventListener("touchstart", onTouchStart, { passive: true });
        window.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("touchend", onTouchEnd, { passive: true });
        window.addEventListener("touchcancel", onTouchEnd, { passive: true });
        return () => {
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
            window.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [pull]);

    if (!isStandalone()) return null;

    const visible = pull > 0 || refreshing;
    const ready = pull >= THRESHOLD || refreshing;

    return (
        <div
            aria-hidden={!visible}
            className="pointer-events-none fixed inset-x-0 top-0 z-[9997] flex justify-center"
            style={{
                transform: `translateY(${visible ? Math.max(pull, refreshing ? THRESHOLD : 0) - 8 : -40}px)`,
                transition: startY.current === null ? "transform 0.2s ease" : "none",
                opacity: visible ? 1 : 0,
            }}
        >
            <div className="mt-2 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card shadow-md">
                <RotateCw
                    className={refreshing ? "h-4 w-4 text-primary animate-spin" : "h-4 w-4 text-foreground/80"}
                    style={{
                        transform: refreshing ? undefined : `rotate(${(pull / MAX_PULL) * 270}deg)`,
                        opacity: ready ? 1 : 0.6,
                    }}
                />
            </div>
        </div>
    );
}
